import { roleAtLeast, type SiteNode, type TenantRole } from "@fts/core";
import type { MessageKey } from "../i18n/tr";
import { vehicleStatus, type VehicleStatus } from "../lib/fleet";
import type { IconName } from "../components/Icon";

export type NavNodeKind = "group" | "item" | "site" | "vehicle";

export type NavNode = {
  id: string;
  kind: NavNodeKind;
  /** Static menu entries are translated; data nodes (sites, vehicles) carry their own label. */
  labelKey?: MessageKey;
  label?: string;
  href?: string;
  icon?: IconName;
  status?: VehicleStatus;
  /** Minimum tenant role needed to see the node. */
  minRole?: TenantRole;
  /** Count shown next to the label (e.g. open alarms); 0 or absent = none. */
  badge?: number;
  /** Only Expartise platform admins see the node (e.g. device inventory). */
  platformOnly?: boolean;
  children?: NavNode[];
};

function siteToNode(s: SiteNode): NavNode {
  return {
    id: `site:${s.id}`,
    kind: "site",
    label: s.name,
    href: `#/sites/${encodeURIComponent(s.id)}`,
    children: [
      ...s.children.map(siteToNode),
      ...s.vehicles.map<NavNode>((v) => ({
        id: `vehicle:${v.id}`,
        kind: "vehicle",
        label: v.fleetNo,
        href: `#/vehicles/${encodeURIComponent(v.id)}`,
        status: vehicleStatus(v),
      })),
    ],
  };
}

/** The full menu before role filtering. */
export function fullMenu(siteTree: SiteNode[]): NavNode[] {
  return [
    { id: "overview", kind: "item", labelKey: "nav.overview", href: "#/", icon: "home" },
    {
      id: "g:monitoring",
      kind: "group",
      labelKey: "nav.group.monitoring",
      children: [
        { id: "map", kind: "item", labelKey: "nav.liveMap", href: "#/map", icon: "map" },
        { id: "alarms", kind: "item", labelKey: "nav.alarms", href: "#/alarms", icon: "bell" },
      ],
    },
    {
      id: "g:fleet",
      kind: "group",
      labelKey: "nav.group.fleet",
      children: [
        {
          id: "sites",
          kind: "item",
          labelKey: "nav.sites",
          href: "#/sites",
          icon: "building",
          children: siteTree.map(siteToNode),
        },
        { id: "vehicles", kind: "item", labelKey: "nav.vehicles", href: "#/vehicles", icon: "truck" },
        { id: "devices", kind: "item", labelKey: "nav.devices", href: "#/devices", icon: "chip" },
      ],
    },
    {
      id: "g:checklist",
      kind: "group",
      labelKey: "nav.group.checklist",
      children: [
        { id: "checklistFaults", kind: "item", labelKey: "nav.checklistFaults", href: "#/checklists/faults", icon: "alert" },
        { id: "checklistTemplates", kind: "item", labelKey: "nav.checklistTemplates", href: "#/checklists/templates", icon: "check" },
      ],
    },
    {
      id: "g:operators",
      kind: "group",
      labelKey: "nav.group.operators",
      children: [
        { id: "operators", kind: "item", labelKey: "nav.operators", href: "#/operators", icon: "user" },
        { id: "cards", kind: "item", labelKey: "nav.cards", href: "#/cards", icon: "card", minRole: "safety" },
        {
          id: "permissions",
          kind: "item",
          labelKey: "nav.permissions",
          href: "#/permissions",
          icon: "key",
          minRole: "safety",
        },
      ],
    },
    {
      id: "g:reports",
      kind: "group",
      labelKey: "nav.group.reports",
      children: [
        { id: "reportUsage", kind: "item", labelKey: "nav.reportUsage", href: "#/reports/usage", icon: "chart" },
        { id: "reportEvents", kind: "item", labelKey: "nav.reportEvents", href: "#/reports/events", icon: "alert" },
        { id: "reportRoutes", kind: "item", labelKey: "nav.reportRoutes", href: "#/reports/routes", icon: "route" },
        { id: "reportChecklist", kind: "item", labelKey: "nav.reportChecklist", href: "#/reports/checklist", icon: "check" },
        { id: "reportHeatmap", kind: "item", labelKey: "nav.reportHeatmap", href: "#/reports/heatmap", icon: "heat" },
        { id: "reportSpaghetti", kind: "item", labelKey: "nav.reportSpaghetti", href: "#/reports/spaghetti", icon: "spaghetti" },
        // Every role that sees reports (owner decision 2026-11-01, ADR 0026); KVKK note on the screen.
        { id: "reportDriving", kind: "item", labelKey: "nav.reportDriving", href: "#/reports/driving", icon: "gauge" },
        // ADR 0034: battery (akü) and lift (direk) from calibrated analog inputs; every role that sees reports.
        { id: "reportBattery", kind: "item", labelKey: "nav.reportBattery", href: "#/reports/battery", icon: "pulse" },
        { id: "reportLift", kind: "item", labelKey: "nav.reportLift", href: "#/reports/lift", icon: "trendUp" },
      ],
    },
    {
      // KYS forms filled in and reported from FTS (ADR 0033); every member role reads them.
      id: "g:kys",
      kind: "group",
      labelKey: "nav.group.kys",
      children: [
        { id: "kysFr12", kind: "item", labelKey: "nav.kysFr12", href: "#/reports/kys-fr12", icon: "log" },
        { id: "kysFr11", kind: "item", labelKey: "nav.kysFr11", href: "#/reports/kys-fr11", icon: "calendar" },
      ],
    },
    {
      id: "g:admin",
      kind: "group",
      labelKey: "nav.group.admin",
      minRole: "admin",
      children: [
        { id: "users", kind: "item", labelKey: "nav.users", href: "#/users", icon: "users", minRole: "admin" },
        { id: "inventory", kind: "item", labelKey: "nav.inventory", href: "#/inventory", icon: "box", minRole: "admin", platformOnly: true },
        { id: "audit", kind: "item", labelKey: "nav.audit", href: "#/audit", icon: "log", minRole: "admin" },
        { id: "settings", kind: "item", labelKey: "nav.settings", href: "#/settings", icon: "settings", minRole: "admin" },
      ],
    },
  ];
}

/** Remove nodes the role may not see; groups left without children are dropped. */
export function filterByRole(nodes: NavNode[], role: TenantRole, platformAdmin = false): NavNode[] {
  const out: NavNode[] = [];
  for (const n of nodes) {
    if (n.minRole && !roleAtLeast(role, n.minRole)) continue;
    if (n.platformOnly && !platformAdmin) continue;
    const children = n.children ? filterByRole(n.children, role, platformAdmin) : undefined;
    if (n.kind === "group" && (!children || children.length === 0)) continue;
    out.push(children ? { ...n, children } : n);
  }
  return out;
}

export function buildMenu(siteTree: SiteNode[], role: TenantRole, platformAdmin = false): NavNode[] {
  return filterByRole(fullMenu(siteTree), role, platformAdmin);
}

/** Set the badge of the node with `id` (anywhere in the tree), returning a new tree. */
export function withBadge(nodes: NavNode[], id: string, badge: number): NavNode[] {
  return nodes.map((n) => {
    if (n.id === id) return { ...n, badge };
    return n.children ? { ...n, children: withBadge(n.children, id, badge) } : n;
  });
}

/** Minimum role per route, used for route guards (keeps menu and guard in sync). */
export const ROUTE_MIN_ROLE: Partial<Record<string, TenantRole>> = {
  users: "admin",
  audit: "admin",
  settings: "admin",
  permissions: "safety",
  cards: "safety",
  inventory: "admin",
};

/** Routes reserved for platform admins (in addition to ROUTE_MIN_ROLE). */
export const PLATFORM_ROUTES: ReadonlySet<string> = new Set(["inventory"]);

/** Ids of the ancestors of the node whose href equals `activeHref` (for auto-expanding). */
export function ancestorsOf(nodes: NavNode[], activeHref: string): string[] | null {
  for (const n of nodes) {
    if (n.href === activeHref) return [];
    if (n.children) {
      const sub = ancestorsOf(n.children, activeHref);
      if (sub) return [n.id, ...sub];
    }
  }
  return null;
}
